import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { api, ApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CheckCircle2, AlertCircle, Loader2, Mail, Globe, Cloud, Bell, ListTree } from "lucide-react";

// "Smoke-test the configuration before relying on it." Every test calls a
// dedicated POST endpoint that exercises the same code path the app would
// at runtime — sendMail() for email, the actual REST client for
// API-Football, putObject() + deleteObject() for S3. Surfaces upstream
// errors verbatim so misconfigs are obvious.

type TestKey = "email" | "apiFootball" | "s3" | "push";
type Status = { state: "idle" | "running" | "ok" | "error"; message?: string; details?: any };

export default function TestPanel() {
  const { user } = useAuth();
  const [emailTo, setEmailTo] = useState(user?.email ?? "");
  const [status, setStatus] = useState<Record<TestKey, Status>>({
    email: { state: "idle" },
    apiFootball: { state: "idle" },
    s3: { state: "idle" },
    push: { state: "idle" },
  });

  const setS = (k: TestKey, s: Status) => setStatus((m) => ({ ...m, [k]: s }));

  const runEmail = async () => {
    if (!emailTo) return toast.error("Bitte Empfänger-Adresse eintragen");
    setS("email", { state: "running" });
    try {
      const r = await api.admin.test.email(emailTo);
      setS("email", { state: "ok", message: `Mail an ${emailTo} verschickt via ${r.backend}` });
    } catch (err) {
      let msg = err instanceof ApiError ? err.message : "Fehler";
      // Server attaches a `hint` for known Microsoft failure modes.
      const hint = (err instanceof ApiError && (err.data as any)?.hint) as string | undefined;
      if (hint) msg = `${msg} — ${hint}`;
      setS("email", { state: "error", message: msg });
    }
  };

  const runApiFootball = async () => {
    setS("apiFootball", { state: "running" });
    try {
      const r = await api.admin.test.apiFootball();
      const used = r.requests?.current ?? "?";
      const limit = r.requests?.limit_day ?? "?";
      setS("apiFootball", {
        state: "ok",
        message: `${r.subscription?.plan ?? "Plan"} · ${used}/${limit} Requests heute`,
        details: r,
      });
    } catch (err) {
      setS("apiFootball", { state: "error", message: err instanceof ApiError ? err.message : "Fehler" });
    }
  };

  const runS3 = async () => {
    setS("s3", { state: "running" });
    try {
      const r = await api.admin.test.s3();
      setS("s3", { state: "ok", message: "Upload + Delete erfolgreich", details: { url: r.url } });
    } catch (err) {
      setS("s3", { state: "error", message: err instanceof ApiError ? err.message : "Fehler" });
    }
  };

  const runPush = async () => {
    setS("push", { state: "running" });
    try {
      const r = await api.admin.test.push();
      if (r.sent > 0) {
        setS("push", { state: "ok", message: `Push an dein Gerät versendet (${r.sent} Abo)` });
      } else if (r.gone > 0) {
        setS("push", {
          state: "error",
          message: `Alle ${r.gone} Abos sind abgelaufen — im Profil → Push deaktivieren + neu aktivieren.`,
        });
      } else {
        setS("push", { state: "error", message: `Versand fehlgeschlagen (${r.failed} Fehler)` });
      }
    } catch (err) {
      setS("push", { state: "error", message: err instanceof ApiError ? err.message : "Fehler" });
    }
  };

  return (
    <section className="bg-card rounded-lg shadow-card p-4 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-display font-bold text-base">Konfiguration testen</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Prüft die aktuell aktiven Einstellungen (DB-Werte überschreiben{" "}
            <code className="font-mono text-[10px] bg-muted px-1 rounded">.env</code>).
          </p>
        </div>
        <QueueStats />
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
        {/* EMAIL */}
        <TestCard
          icon={<Mail className="h-4 w-4" />}
          title="E-Mail"
          subtitle="SMTP oder O365 OAuth2"
          status={status.email}
        >
          <div className="flex gap-2">
            <Input
              type="email"
              placeholder="empfaenger@example.com"
              value={emailTo}
              onChange={(e) => setEmailTo(e.target.value)}
              className="h-9 text-sm"
            />
            <Button
              size="sm"
              onClick={runEmail}
              disabled={status.email.state === "running"}
              className="h-9 bg-accent text-accent-foreground hover:bg-accent/90 font-bold whitespace-nowrap"
            >
              {status.email.state === "running" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Senden"}
            </Button>
          </div>
        </TestCard>

        {/* API-FOOTBALL */}
        <TestCard
          icon={<Globe className="h-4 w-4" />}
          title="API-Football"
          subtitle="Hits /status (kein Quota-Verbrauch)"
          status={status.apiFootball}
        >
          <Button
            size="sm"
            onClick={runApiFootball}
            disabled={status.apiFootball.state === "running"}
            className="h-9 bg-accent text-accent-foreground hover:bg-accent/90 font-bold w-full"
          >
            {status.apiFootball.state === "running" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Verbindung prüfen"}
          </Button>
        </TestCard>

        {/* S3 */}
        <TestCard
          icon={<Cloud className="h-4 w-4" />}
          title="AWS S3"
          subtitle="Upload + Delete einer 2-Byte-Datei"
          status={status.s3}
        >
          <Button
            size="sm"
            onClick={runS3}
            disabled={status.s3.state === "running"}
            className="h-9 bg-accent text-accent-foreground hover:bg-accent/90 font-bold w-full"
          >
            {status.s3.state === "running" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Bucket testen"}
          </Button>
        </TestCard>

        {/* Web Push — sends to the calling admin's own subscriptions */}
        <TestCard
          icon={<Bell className="h-4 w-4" />}
          title="Web Push"
          subtitle="Sendet an deine eigenen Abos (Profil → Push aktivieren)"
          status={status.push}
        >
          <Button
            size="sm"
            onClick={runPush}
            disabled={status.push.state === "running"}
            className="h-9 bg-accent text-accent-foreground hover:bg-accent/90 font-bold w-full"
          >
            {status.push.state === "running" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Test-Push senden"}
          </Button>
        </TestCard>
      </div>
    </section>
  );
}

// Compact widget showing the BullMQ notification queue state. Polls every
// 5s while the panel is mounted. Shows a "Sync-Modus" badge when Redis
// isn't configured (push reminders run inline in the cron tick instead).
function QueueStats() {
  const [data, setData] = useState<Awaited<ReturnType<typeof api.admin.queues.notifications>> | null>(null);
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await api.admin.queues.notifications();
        if (!cancelled) setData(r);
      } catch {
        /* admin endpoint, polling — silent failures fine */
      }
    };
    poll();
    const id = setInterval(poll, 5000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  if (!data) return null;
  if (!data.configured) {
    return (
      <div
        className="text-[10px] font-bold text-muted-foreground bg-muted rounded-md px-2 py-1.5 whitespace-nowrap"
        title="Redis ist nicht konfiguriert — Push-Erinnerungen laufen synchron im Cron-Tick"
      >
        <ListTree className="inline h-3 w-3 mr-1" />
        Sync-Modus
      </div>
    );
  }
  const c = data.counts!;
  return (
    <div
      className="text-[10px] font-bold rounded-md bg-muted px-2 py-1.5 flex items-center gap-2 whitespace-nowrap"
      title="Notification Queue: waiting / active / failed / completed"
    >
      <ListTree className="h-3 w-3 shrink-0" />
      <span>Queue:</span>
      <span className="text-foreground tabular-nums">{c.waiting}w</span>
      <span className="text-accent tabular-nums">{c.active}a</span>
      {c.failed > 0 && <span className="text-destructive tabular-nums">{c.failed}f</span>}
      <span className="text-success tabular-nums">{c.completed}✓</span>
    </div>
  );
}

function TestCard({
  icon,
  title,
  subtitle,
  status,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  status: Status;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-md border border-border p-3 space-y-2.5">
      <div className="flex items-center gap-2">
        <div className="bg-muted rounded-md p-1.5">{icon}</div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold leading-tight">{title}</div>
          <div className="text-[10px] text-muted-foreground leading-tight">{subtitle}</div>
        </div>
      </div>
      {children}
      {status.state !== "idle" && (
        <div
          className={`text-xs rounded-md px-2 py-1.5 flex items-start gap-1.5 ${
            status.state === "ok"
              ? "bg-success/10 text-success"
              : status.state === "error"
                ? "bg-destructive/10 text-destructive"
                : "bg-muted text-muted-foreground"
          }`}
        >
          {status.state === "ok" ? (
            <CheckCircle2 className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          ) : status.state === "error" ? (
            <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          ) : (
            <Loader2 className="h-3.5 w-3.5 shrink-0 mt-0.5 animate-spin" />
          )}
          <span className="break-words">{status.message ?? status.state}</span>
        </div>
      )}
    </div>
  );
}
